import type { FC, ReactElement } from "react"

import { motion } from "framer-motion"

import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { type TUseInteractionState, useInteractionState } from "@/3514/hooks"
import { buildCssClassName, getTransitionTwClasses, withPressHandler } from "@/3514/utils"
import type { TEmptyCallback } from "@/shared/types/functions"

type TProps = { withAccent?: boolean; handleAction: TEmptyCallback; isNotAccessible?: boolean }

const classes = {
    container: ({ isNotAccessible }: { isNotAccessible: boolean }): string =>
        buildCssClassName("!outline-none", isNotAccessible || "[pointer-events:auto]", getTransitionTwClasses()),
    badge: (isActive: boolean, withAccent: boolean): string =>
        buildCssClassName(
            "py-[1px] px-[5px] rounded-[5px]",
            getTransitionTwClasses("colors"),
            isActive ? "bg-white" : [withAccent ? "bg-accent" : "bg-blue-200"]
        )
}

const BetaMark: FC<TProps> = ({ withAccent = false, handleAction, isNotAccessible = false }: TProps): ReactElement => {
    const {
        actions: { onMouseEnter, onMouseLeave, onFocus, onBlur },
        isActionAffected
    }: TUseInteractionState = useInteractionState()

    const onAction: ReturnType<typeof withPressHandler> = withPressHandler(handleAction)

    return (
        <motion.button
            type="button"
            whileFocus={{ scale: 1.075 }}
            whileHover={{ scale: 1.075 }}
            tabIndex={isNotAccessible ? -1 : 0}
            transition={{ duration: 0.2, ease: "easeIn" }}
            className={classes.container({ isNotAccessible })}
            onKeyDown={onAction}
            onClick={onAction}
            {...(isNotAccessible ? {} : { onFocus, onBlur, onMouseEnter, onMouseLeave })}
        >
            <Typography
                text={withAccent ? "BETA" : "beta"}
                color={isActionAffected ? ETypographyColor.Accent : ETypographyColor.White}
                weight={withAccent ? ETypographyWeight.Bold : ETypographyWeight.Default}
                isItalic={!withAccent}
                tag={ETypographyTag.Paragraph}
                size={ETypographySize.Tiny}
                className={classes.badge(isActionAffected, withAccent)}
            />
        </motion.button>
    )
}

BetaMark.displayName = "BetaMark"

export { BetaMark }
